/**
 * Pill de saldo CLICKABLE de la Home + orquestación de los overlays del wallet.
 *
 * Drop-in para HomeMobile y HomeDesktop: recibe el `balance` (que ya cargó
 * `useCredits` en la pantalla) y maneja internamente:
 *   - hoja de saldo (`BalanceSheet`) con los últimos movimientos,
 *   - historial completo (`WalletHistoryScreen`),
 *   - detalle: si la fila es una sesión → `SessionCostSheet` (desglose real);
 *     si es una recarga/bono → `TxnDetailSheet` (método · fecha · estado).
 *
 * Los movimientos se cargan lazy (`useWallet(enabled)`) recién al abrir.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { formatOrbs } from '@orborix/shared-types';
import type { CreditsBalance } from '../lib/billingApi.js';
import { SessionCostSheet } from '../screens/activity/SessionCostSheet.js';
import { useWallet } from './useWallet.js';
import { balanceOrbsDisplay, type WalletTxn } from './walletModel.js';
import { AddCreditsSheet } from './AddCreditsSheet.js';
import { isRechargeOffered } from './rechargeAvailability.js';
import { BalanceSheet, IconOrb, TxnDetailSheet, WalletHistoryScreen } from './WalletSheets.js';
import './wallet.css';

/** Umbral de "saldo bajo" (ámbar): $0,50 = 50_000_000 nano = 500 orbs. */
const LOW_BALANCE_NANO = 50_000_000n;

export interface WalletButtonProps {
  balance: CreditsBalance | null;
  loading: boolean;
  /** Error de red al leer el saldo (de `useCredits`). Habilita la pill de reintento. */
  error?: Error | null;
  /** Reintenta el fetch del saldo (`useCredits().refresh`). */
  onRetry?: () => void;
  /** `compact` (header mobile) · `comfortable` (topbar desktop). */
  size?: 'compact' | 'comfortable';
}

export function WalletButton({
  balance,
  loading,
  error,
  onRetry,
  size = 'compact',
}: WalletButtonProps): ReactElement | null {
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Hoja "Cargar créditos" (packs + compra in-app).
  const [addOpen, setAddOpen] = useState(false);
  // Detalle de una recarga/bono (sin sesión).
  const [detailTxn, setDetailTxn] = useState<WalletTxn | null>(null);
  // Detalle de un consumo (sesión) → desglose real con SessionCostSheet.
  const [costSessionId, setCostSessionId] = useState<string | null>(null);

  const { txns, loading: txnsLoading } = useWallet(balanceOpen || historyOpen);
  const now = useMemo(() => new Date(), [balanceOpen, historyOpen]);

  if (loading && !balance) {
    return <span className={`wallet-pill wallet-pill--${size} is-skeleton`} aria-hidden="true" />;
  }
  if (!balance) {
    // Error de red sin saldo cacheado: en vez de DESAPARECER (perdés la pista
    // del saldo sin saber por qué), pill atenuada que reintenta al tocar. Sin
    // `error`/`onRetry` (nunca cargó, sin fallo explícito) → null como antes.
    if (error && onRetry) {
      return (
        <button
          type="button"
          className={`wallet-pill wallet-pill--${size} is-error`}
          onClick={onRetry}
          aria-label="No se pudo cargar el saldo. Reintentar"
          title="No se pudo cargar el saldo. Reintentar"
        >
          <span className="wallet-pill__ic" aria-hidden="true">
            <IconOrb />
          </span>
          <span className="wallet-pill__amount" aria-hidden="true">
            ↻
          </span>
        </button>
      );
    }
    return null;
  }

  const nano = BigInt(balance.balanceNano);
  const isNegative = nano < 0n;
  const isLow = nano >= 0n && nano < LOW_BALANCE_NANO;
  const display = formatOrbs(nano);
  const stateClass = isNegative ? 'is-negative' : isLow ? 'is-low' : '';

  // No "Agregar saldo" where the app does not sell orbs (native build without its store key).
  const onAddFunds = isRechargeOffered() ? () => setAddOpen(true) : null;

  const onTxn = (txn: WalletTxn): void => {
    if (txn.sessionId) setCostSessionId(txn.sessionId);
    else setDetailTxn(txn);
  };

  return (
    <>
      <button
        type="button"
        className={`wallet-pill wallet-pill--${size} ${stateClass}`}
        onClick={() => setBalanceOpen(true)}
        aria-label={`Saldo: ${display} orbs. Ver movimientos`}
        title={`Saldo: ${display} orbs`}
      >
        <span className="wallet-pill__ic" aria-hidden="true">
          <IconOrb />
        </span>
        <span className="wallet-pill__amount">{display}</span>
      </button>

      {balanceOpen && (
        <BalanceSheet
          balanceOrbs={balanceOrbsDisplay(balance)}
          txns={txns}
          loading={txnsLoading}
          now={now}
          onClose={() => setBalanceOpen(false)}
          onSeeAll={() => setHistoryOpen(true)}
          onTxn={onTxn}
          onAddFunds={onAddFunds}
        />
      )}
      {historyOpen && (
        <WalletHistoryScreen
          balanceOrbs={balanceOrbsDisplay(balance)}
          txns={txns}
          loading={txnsLoading}
          now={now}
          onBack={() => setHistoryOpen(false)}
          onTxn={onTxn}
          onAddFunds={onAddFunds}
        />
      )}
      {addOpen && <AddCreditsSheet onClose={() => setAddOpen(false)} />}
      {detailTxn && <TxnDetailSheet txn={detailTxn} now={now} onClose={() => setDetailTxn(null)} />}
      <SessionCostSheet
        open={costSessionId !== null}
        sessionId={costSessionId}
        onClose={() => setCostSessionId(null)}
      />
    </>
  );
}
